import React, { useState, type FC } from 'react';

import styled from '@emotion/styled';

import { Badge, Button, InputGroup } from '@shared/components/bootstrap';
import { InputFormControl } from '@shared/components/fields/input_form_control';

export type TagsFormControlProps = {
  id: string;
  name: string;
  placeholder: string;
  value: string[];
  onChange(tags: string[]): void;
};

const TagsFormControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagsFormControlList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TagsFormControlBadgeContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const TagsFormControlBadgeDelete = styled.button`
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 -4px 0 0;
  color: #fff;
`;

const TagsFormControlBadge: FC<{
  tag: string;
  onDelete(tag: string): void;
}> = ({ tag, onDelete }) => (
  <Badge key={tag} style="info">
    <TagsFormControlBadgeContent>
      <span>{tag}</span>
      <TagsFormControlBadgeDelete type="button" onClick={() => onDelete(tag)} aria-label="Remove">
        <span>&times;</span>
      </TagsFormControlBadgeDelete>
    </TagsFormControlBadgeContent>
  </Badge>
);

export const TagsFormControl: React.FC<TagsFormControlProps> = ({ id, name, placeholder, value, onChange }) => {
  const [text, setText] = useState<string>('');

  const blocked = !text.trim() || value.includes(text.trim());

  const onSave = () => {
    if (blocked) return;

    setText('');
    onChange([...value, text.trim()]);
  };

  const onDelete = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      onSave();
    }
  };

  return (
    <TagsFormControlContainer>
      <InputGroup>
        <InputFormControl
          id={id}
          name={name}
          placeholder={placeholder}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <InputGroup.Button>
          <Button type="button" kind="info" onClick={onSave} disabled={blocked}>
            Add
          </Button>
        </InputGroup.Button>
      </InputGroup>
      <TagsFormControlList>
        {value.map((tag, key) => (
          <TagsFormControlBadge key={key} tag={tag} onDelete={onDelete} />
        ))}
      </TagsFormControlList>
    </TagsFormControlContainer>
  );
};
